import { IconInfo16 } from '@warp-ds/icons/react/info-16';
import { IconUser16 } from '@warp-ds/icons/react/user-16';
import { Button, TextField } from '@warp-ds/react';
import { Attention } from '@warp-ds/react/components/attention';
import cn from 'classnames';
import { type FormEvent, useCallback, useReducer, useRef, useState } from 'react';
import { useConfig } from '../../config/ConfigContext';
import { t } from '../../locale/texts';
import { getOneYearFromToday } from '../../util/datetime-fns';
import { Autocomplete } from '../common/autocomplete/Autocomplete';
import { hotelAutocompleteMapper } from '../common/autocomplete/utils/AutocompleteMapper';
import { Dropdown } from '../common/Dropdown';
import { FrontPageModal } from '../common/frontPageModal/FrontPageModal';
import { FrontpageDatePickerWrapper } from '../common/frontpageDatePickerWrapper/FrontpageDatePickerWrapper';
import { storeLastSearchHotel } from '../common/lastSearch';
import { SearchPanelWrapper } from '../common/SearchPanelWrapper';
import { trackSearchClick } from '../common/tracking';
import type { AutocompleteOption } from '../types';
import { HotelRooms } from './HotelRooms';
import styles from './HotelSearch.module.css';
import { useHotelInitialState } from './hooks/useHotelInitialState';
import { useHotelValidation } from './hooks/useHotelValidation';
import actions, { reducer } from './hotelActions';
import { hotelDefaultState } from './hotelDefaultState';
import { buildHotelTrackingItem } from './utils/buildHotelTrackingItem';
import { formatRoomInfoText } from './utils/formatRoomInfoText';
import { HotelHiddenInputs } from './utils/HotelHiddenInputs';

type HotelSearchProps = {
    prefill?: Partial<typeof hotelDefaultState>;
    position: string;
    unleash?: Record<string, boolean> | null; // cannot be just undefined as that is not serializable (toggles are returned from getSSP)
};

export const HotelSearch = ({ prefill, position, unleash }: HotelSearchProps) => {
    const [pristine, setPristine] = useState(true);
    const [state, dispatch] = useReducer(reducer, { ...hotelDefaultState, ...prefill });
    const [showVioInfo, setShowVioInfo] = useState(false);
    const vioInfoTargetEl = useRef(null);

    const { hotelAutocompleteUrl, hotelVioSearchUrl } = useConfig();

    useHotelInitialState(dispatch, prefill);

    const { rooms = [], startDate, endDate, destination, destinationContext } = state;
    const { isValid, invalid } = useHotelValidation(state, pristine);

    const handleDestinationSelect = useCallback((option: AutocompleteOption | null) => dispatch(actions.setDestination(option)), []);

    const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
        if (isValid) {
            const item = buildHotelTrackingItem({ destinationContext, rooms, startDate, endDate });
            trackSearchClick(
                'Search hotel',
                '/reise/hotell',
                'hotel',
                `${position} - ${destinationContext?.objectId ? 'vio' : 'whitelabel'}`,
                item,
            );
            storeLastSearchHotel(state);
        } else {
            setPristine(false);
            event.preventDefault();
        }
    };

    return (
        <SearchPanelWrapper
            onSubmit={handleSubmit}
            action={destinationContext?.objectId ? hotelVioSearchUrl : 'https://travel-kayak-wl.finn.no/in'}
            method="GET"
            dataTestId="hotelSearch">
            {unleash?.kayakWhitelabel ? null : (
                /* biome-ignore lint/a11y/useSemanticElements: using <button... would style it differently */
                <div
                    className="flex justify-end gap-6"
                    ref={vioInfoTargetEl}
                    role="button"
                    tabIndex={0}
                    aria-describedby="powered-by-tooltip"
                    aria-expanded={showVioInfo}
                    onClick={() => setShowVioInfo(true)}
                    onMouseLeave={() => setShowVioInfo(false)}
                    onBlur={() => setShowVioInfo(false)}>
                    <span>Levert av</span>
                    <svg xmlns="http://www.w3.org/2000/svg" width="59" height="20" viewBox="0 0 59 20" fill="none">
                        <title>Vio</title>
                        <g clip-path="url(#clip0_20_7032)">
                            <g clip-path="url(#clip1_20_7032)">
                                <path
                                    d="M41.4486 5.1369C42.8605 5.1369 44.005 3.98697 44.005 2.56845C44.005 1.14994 42.8605 0 41.4486 0C40.0367 0 38.8921 1.14994 38.8921 2.56845C38.8921 3.98697 40.0367 5.1369 41.4486 5.1369Z"
                                    fill="#7130C9"
                                />
                                <path
                                    d="M31.3933 14.8139L28.535 6.43695H24.1403L28.9584 19.7337H33.7494L38.4336 6.43695H34.2254L31.3933 14.8139Z"
                                    fill="#7130C9"
                                />
                                <path d="M43.4626 6.43695H39.3864V19.7328H43.4626V6.43695Z" fill="#7130C9" />
                                <path
                                    d="M51.9063 6.17149C47.9089 6.17149 44.8126 9.20318 44.8126 13.0857C44.8126 16.9683 47.9089 20 51.9063 20C55.9037 20 59 16.9683 59 13.0857C59 9.20318 55.9037 6.17149 51.9063 6.17149ZM51.9063 16.1438C50.1854 16.1438 48.8887 14.8147 48.8887 13.0857C48.8887 11.3567 50.1591 10.0277 51.9063 10.0277C53.6535 10.0277 54.9238 11.3576 54.9238 13.0857C54.9238 14.8139 53.6272 16.1438 51.9063 16.1438Z"
                                    fill="#7130C9"
                                />
                                <path
                                    d="M7.33782 5.05164C7.42443 5.29073 7.52592 5.52103 7.64053 5.74078C8.92752 8.2143 11.8707 9.40887 14.5523 8.42702L12.7727 3.52217C12.6914 3.29715 12.4438 3.182 12.2198 3.26375L7.33782 5.05164Z"
                                    fill="#9865FC"
                                />
                                <path
                                    d="M22.4352 4.5383C22.3389 2.07709 20.3354 0.0808685 17.8848 0.00263701C17.6731 -0.00439502 17.464 0.00263702 17.2592 0.0237331L17.7089 1.26928C18.9373 1.38619 19.8857 2.40935 19.8857 3.65666C19.8857 4.22186 19.688 4.74135 19.3581 5.15185C19.0187 5.57377 18.38 5.58871 18.0064 5.19668L16.8358 3.94761C16.5751 3.66985 16.2128 3.5125 15.8323 3.51162L12.7666 3.50987L14.5514 8.42878C13.6581 9.10034 12.5374 9.50468 11.3361 9.50468C9.11037 9.50468 7.20396 8.13959 6.3973 6.19698C2.37274 7.8961 3.71747 11.0649 0.568687 11.7453C0.202103 11.8244 0 11.7971 0 11.7971L0.289593 12.5961C2.0464 12.6304 3.19777 11.7936 3.33251 11.6688L6.24594 19.7293H9.3396L9.33785 16.3064C9.33785 16.0418 9.57232 15.8388 9.83304 15.8783L12.9258 16.3442C13.0649 16.3653 13.1848 16.4523 13.2478 16.5789L14.8156 19.7293H18.6372L15.3353 10.6061C15.3721 10.6105 16.5812 10.7397 17.3143 10.3019L15.9959 9.12847C16.5331 9.34294 17.1184 9.46073 17.7317 9.46073C20.3949 9.46073 22.5419 7.23861 22.436 4.53918L22.4352 4.5383ZM16.2225 6.32268C15.9994 6.32268 15.8191 6.14161 15.8191 5.91746C15.8191 5.69331 15.9994 5.51224 16.2225 5.51224C16.4456 5.51224 16.6258 5.69331 16.6258 5.91746C16.6258 6.14161 16.4456 6.32268 16.2225 6.32268Z"
                                    fill="#7130C9"
                                />
                                <path
                                    d="M14.5514 8.4279C11.8689 9.40975 8.92577 8.2143 7.63878 5.7399C7.63878 5.7399 7.56442 5.76188 7.56092 5.76276C6.91524 5.94998 6.39642 6.19611 6.39642 6.19611C7.20308 8.13783 9.11037 9.5038 11.3353 9.5038C12.5365 9.5038 13.6572 9.09946 14.5505 8.4279H14.5514Z"
                                    fill="#3D0083"
                                />
                            </g>
                        </g>
                        <defs>
                            <clipPath id="clip0_20_7032">
                                <rect width="59" height="20" fill="white" />
                            </clipPath>
                            <clipPath id="clip1_20_7032">
                                <rect width="59" height="20" fill="white" />
                            </clipPath>
                        </defs>
                    </svg>
                    <IconInfo16 />
                    <Attention tooltip placement="bottom-end" isShowing={showVioInfo} targetEl={vioInfoTargetEl}>
                        <div id="powered-by-tooltip" className="w-[400]">
                            <p className="font-bold">Denne hotelltjenesten leveres av vår partner Vio.com.</p>
                            FINN er ansvarlig for partnerskapet og samarbeidet, men Vio er behandlingsansvarlig for cookies og personlig
                            informasjon som behandles hos dem.
                        </div>
                    </Attention>
                </div>
            )}
            <div className={styles['form-body']}>
                <Autocomplete
                    vertical="hotel"
                    id="destination"
                    label={t('hotel.destination.label')}
                    placeholder={t('hotel.destination.placeholder')}
                    defaultValue={destination}
                    onSelect={handleDestinationSelect}
                    url={hotelAutocompleteUrl}
                    mapOptions={hotelAutocompleteMapper}
                    error={invalid.destination ? t('hotel.validation.destination') : undefined}
                />
                <FrontpageDatePickerWrapper
                    upperDateLimit={getOneYearFromToday()}
                    startDate={startDate}
                    endDate={endDate}
                    useRange
                    useAnchor={{
                        invalid: invalid.startDate || invalid.endDate || invalid.dateRange,
                        helpText: invalid.startDate || invalid.endDate || invalid.dateRange ? 'Du må velge dato' : undefined,
                    }}
                    onDateChange={({ startDate, endDate }) => {
                        dispatch(actions.setDates({ startDate, endDate }));
                    }}>
                    <TextField
                        id="date-range-start"
                        data-testid="Hotel-dateRangeStart"
                        label={t('hotel.checkin.label')}
                        invalid={invalid.startDate || invalid.dateRange}
                        helpText={invalid.startDate && t('hotel.validation.dateRangeStart')}
                    />
                    <TextField
                        id="date-range-end"
                        data-testid="Hotel-dateRangeEnd"
                        label={t('hotel.checkout.label')}
                        invalid={invalid.endDate || invalid.dateRange}
                        helpText={
                            invalid.endDate
                                ? t('hotel.validation.dateRangeEnd')
                                : invalid.dateRange
                                  ? t('hotel.validation.dateRangeGap')
                                  : undefined
                        }
                    />
                </FrontpageDatePickerWrapper>
                <FrontPageModal
                    breakpoint="sm"
                    title="Gjester og rom"
                    trigger={{
                        text: formatRoomInfoText(rooms),
                        prefix: <IconUser16 aria-hidden />,
                    }}
                    error={invalid.rooms ? t('hotel.validation.rooms') : undefined}>
                    <HotelRooms rooms={rooms} dispatch={dispatch} />
                </FrontPageModal>
                <Dropdown
                    trigger={{ text: formatRoomInfoText(rooms), label: 'Reisende' }}
                    prefix={<IconUser16 aria-hidden />}
                    className="hidden sm:flex">
                    <HotelRooms rooms={rooms} dispatch={dispatch} />
                </Dropdown>
                <HotelHiddenInputs destinationContext={destinationContext} startDate={startDate} endDate={endDate} rooms={rooms} />
                <Button
                    className={cn(styles['search-button'], {
                        [styles['search-button--error']]: invalid && Object.values(invalid).some((v) => v),
                    })}
                    fullWidth
                    type="submit"
                    primary
                    data-testid="hotelSearchButton">
                    {t('action.search')}
                </Button>
            </div>
        </SearchPanelWrapper>
    );
};
